import { COUNTER_CONFIRMATIONS, type ShipTime, wallInstants } from '@felix/domain';
import { zoneOffsetMinutes } from '../personnel/rest-hours.js';
import { errorDetail } from './source.js';
import type { CounterInfo, CounterSuggestion, ReadingInput } from './types.js';

/**
 * Sayaç diyaloğunun formu (M7a Task 6b; K2, K4, K5): sayaç değeri ya da son okumadan bu yana,
 * "Replace meter" sekmesi ve Noon Report önerisi. [NR496 4.5.1] kontrollerini gemi sunucusu yapar;
 * eksik onayları hata ayrıntısında döner, diyalog onları ayrı pencerede tek tek sorar.
 */

export type HoursField = number | '';

export interface CounterFormValues {
  tab: 'reading' | 'replace';
  mode: 'total' | 'delta';
  totalHours: HoursField;
  deltaHours: HoursField;
  /** Gemi saatiyle `YYYY-MM-DDTHH:mm`. */
  readAt: string;
  kind: 'meter_replaced' | 'meter_reset';
  previousMeterFinal: HoursField;
  meterStartHours: HoursField;
  reason: string;
  /** Noon Report önerisi kullanıldıysa raporun değeri ve kimliği (K2). */
  suggestion: { hours: number; reportId: string } | null;
}

export function emptyCounterForm(readAt: string): CounterFormValues {
  return {
    tab: 'reading',
    mode: 'total',
    totalHours: '',
    deltaHours: '',
    readAt,
    kind: 'meter_replaced',
    previousMeterFinal: '',
    meterStartHours: '',
    reason: '',
    suggestion: null,
  };
}

const MINUTE = 60_000;

/**
 * Gemi saatiyle `YYYY-MM-DDTHH:mm` → UTC ISO-8601 (CLAUDE.md "Zaman"): saat kaydı varsa onun
 * farkıyla, tekrar eden saatte ilk oluşum (ADR 0065 §4, §7).
 */
export function shipLocalToUtc(local: string, timeZone: ShipTime): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(local);
  if (!match) return null;
  const wall = Date.parse(`${match[1]}T${match[2]}:00.000Z`);
  if (Number.isNaN(wall)) return null;
  if (typeof timeZone !== 'string') {
    const minute = Number(match[2]?.slice(0, 2)) * 60 + Number(match[2]?.slice(3, 5));
    const first = wallInstants(timeZone, match[1] ?? '', minute)[0];
    if (first !== undefined) return new Date(first).toISOString();
  }
  const guess = wall - zoneOffsetMinutes(timeZone, wall) * MINUTE;
  return new Date(wall - zoneOffsetMinutes(timeZone, guess) * MINUTE).toISOString();
}

/** UTC an → gemi saatiyle `YYYY-MM-DDTHH:mm`. */
export function utcToShipLocal(at: string | number | Date, timeZone: ShipTime): string {
  const ms = at instanceof Date ? at.getTime() : typeof at === 'number' ? at : Date.parse(at);
  return new Date(ms + zoneOffsetMinutes(timeZone, ms) * MINUTE).toISOString().slice(0, 16);
}

/** Noon Report önerisini forma koyar (değer, raporun anı, kaynak); hiçbir şey yazmaz (K2). */
export function withSuggestion(
  values: CounterFormValues,
  suggestion: CounterSuggestion,
  timeZone: ShipTime,
): CounterFormValues & { source: 'noon_report'; sourceId: string } {
  return {
    ...values,
    tab: 'reading',
    mode: 'total',
    totalHours: suggestion.hours,
    readAt: utcToShipLocal(suggestion.reportedAt, timeZone),
    suggestion: { hours: suggestion.hours, reportId: suggestion.reportId },
    source: 'noon_report',
    sourceId: suggestion.reportId,
  };
}

const round1 = (value: number) => Math.round(value * 10) / 10;

/**
 * Formdan okuma girdisi; eksik alanda `null`. "Son okumadan bu yana" son değere eklenir. Olay
 * satırında sayaç değeri yeni sayacın başlangıcıdır (K4 sözleşmesi). Öneri değeri elle değişirse
 * kaynak elle girişe döner.
 */
export function toReadingInput(
  values: CounterFormValues,
  context: { counter: CounterInfo | null; timeZone: ShipTime; confirmations: readonly string[] },
): ReadingInput | null {
  const readAt = shipLocalToUtc(values.readAt, context.timeZone);
  if (readAt === null) return null;
  if (values.tab === 'replace') {
    if (values.previousMeterFinal === '' || values.meterStartHours === '') return null;
    return {
      kind: values.kind,
      readAt,
      totalHours: values.meterStartHours,
      previousMeterFinal: values.previousMeterFinal,
      meterStartHours: values.meterStartHours,
      reason: values.reason.trim() === '' ? null : values.reason.trim(),
      confirmations: [...context.confirmations],
      source: 'manual',
      sourceId: null,
    };
  }
  let total: number;
  if (values.mode === 'delta') {
    if (values.deltaHours === '') return null;
    total = round1((context.counter?.lastReading?.totalHours ?? 0) + values.deltaHours);
  } else {
    if (values.totalHours === '') return null;
    total = values.totalHours;
  }
  const fromNoon =
    values.mode === 'total' &&
    values.suggestion !== null &&
    values.suggestion.hours === values.totalHours;
  return {
    kind: 'reading',
    readAt,
    totalHours: total,
    confirmations: [...context.confirmations],
    source: fromNoon ? 'noon_report' : 'manual',
    sourceId: fromNoon ? (values.suggestion?.reportId ?? null) : null,
  };
}

/** Sunucunun istediği onaylar (hata ayrıntısı `LOWER_THAN_PREVIOUS,…`); bilinmeyen kod atlanır. */
export function requiredConfirmations(error: unknown): string[] {
  const code = (error as { code?: unknown } | null)?.code;
  if (code !== 'CONFIRMATION_REQUIRED') return [];
  const known = new Set<string>(COUNTER_CONFIRMATIONS);
  return (errorDetail(error) ?? '').split(',').filter((c) => known.has(c));
}

/**
 * Onay metinlerinin değerleri: önceki okuma, karşılaştırılan değer, günlük ortalama ve ekipmanın
 * sınırı. Sunucuyla aynı (`checkCounterReading`): okumada sayacın değeri, sayaç değişiminde eski
 * sayacın son değeri karşılaştırılır; yeni sayacın başlangıcı değil (M7A-REG-3) [NR496 4.5.1c,
 * 4.5.1d].
 */
export function confirmationParams(
  reading: Pick<ReadingInput, 'kind' | 'totalHours' | 'previousMeterFinal' | 'readAt'>,
  counter: CounterInfo | null,
  locale = 'en',
): { previous: string; value: string; perDay: string; limit: string } {
  const format = new Intl.NumberFormat(locale === 'en' ? 'en-US' : locale, {
    maximumFractionDigits: 1,
  });
  const compared =
    reading.kind === 'reading' ? reading.totalHours : (reading.previousMeterFinal ?? null);
  const last = counter?.lastReading ?? null;
  const days = last ? (Date.parse(reading.readAt) - Date.parse(last.readAt)) / 86_400_000 : 0;
  const perDay = last && compared !== null && days > 0 ? (compared - last.totalHours) / days : 0;
  return {
    previous: last ? format.format(last.totalHours) : '—',
    value: compared === null ? '—' : format.format(compared),
    perDay: format.format(round1(perDay)),
    limit: format.format(counter?.maxDailyHours ?? 24),
  };
}

/**
 * Onay metninin anahtarı: sayaç değişiminde olaya özgü metin. Kullanıcının işaretlediği beyan
 * değiştirilemez satıra yazılır; değişimi kaydederken "sayaç değiştirilmedi" diye onaylatılmaz.
 */
export function confirmationKey(code: string, kind: ReadingInput['kind']): string {
  return kind === 'reading' ? `pms.confirmations.${code}` : `pms.meterChangeConfirmations.${code}`;
}

/**
 * Saat alanının biçimi dile göre (M7A-SCR-5): önceki okuma "12.845 s" görünürken alan "12,900"
 * gruplamamalı; Türkçede binlik nokta, ondalık virgül. Yalnız dilin ondalık ayracı kabul edilir:
 * binlik ayraç yazılınca ondalığa dönmez.
 */
export function hoursInputFormat(language: string | undefined): {
  thousandSeparator: string;
  decimalSeparator: string;
  allowedDecimalSeparators: string[];
} {
  return language === 'tr'
    ? { thousandSeparator: '.', decimalSeparator: ',', allowedDecimalSeparators: [','] }
    : { thousandSeparator: ',', decimalSeparator: '.', allowedDecimalSeparators: ['.'] };
}

export const HISTORY_PAGE_SIZE = 12;

/**
 * History sekmesinin bir sayfası (M7A-SCR-2): bütün okumalar sayfalarla erişilir [NR496 4.3.2a];
 * tarih sırası seçilir. Sınır dışı sayfa son sayfaya iner.
 */
export function readingHistoryPage<T extends { readAt: string; id: string }>(
  readings: readonly T[],
  page: number,
  order: 'asc' | 'desc',
  size = HISTORY_PAGE_SIZE,
): { rows: T[]; page: number; pages: number; from: number; to: number; total: number } {
  const sorted = [...readings].sort(
    (a, b) =>
      (order === 'asc' ? 1 : -1) *
      (Date.parse(a.readAt) - Date.parse(b.readAt) || a.id.localeCompare(b.id)),
  );
  const total = sorted.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, page), pages);
  const start = (current - 1) * size;
  const rows = sorted.slice(start, start + size);
  return {
    rows,
    page: current,
    pages,
    from: total === 0 ? 0 : start + 1,
    to: start + rows.length,
    total,
  };
}
